import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/layout/DashboardLayout';
import TenantInfo from '../components/TenantInfo';
import Container from '../components/layout/Container';
import { dashboardService, DashboardStats } from '../services/dashboardService';
import SearchBar from '../components/layout/SearchBar';
import DateRangePicker from '../components/layout/DateRangePicker';
import { ticketService } from '../services/ticketService';
import { loadDateRange, saveDateRange } from '../utils/dateRangePersistence';
import { useTheme } from '../contexts/ThemeContext';
import EnhancedTicketTrends from '../components/dashboard/EnhancedTicketTrends';
import TicketStatusBreakdown from '../components/dashboard/TicketStatusBreakdown';
import PriorityDistribution from '../components/dashboard/PriorityDistribution';
import PerformanceMetrics from '../components/dashboard/PerformanceMetrics';
import TopContributorsCard from '../components/dashboard/TopContributorsCard';
import OmnichannelOverview from '../components/dashboard/OmnichannelOverview';
import AgentPerformance from '../components/dashboard/AgentPerformance';

interface EnhancedDashboardPageProps {}

export default function EnhancedDashboardPage({}: EnhancedDashboardPageProps) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const [dateRange, setDateRange] = useState(loadDateRange('enhanced-dashboard-date-range'));

  // Save date range whenever it changes
  useEffect(() => {
    saveDateRange('enhanced-dashboard-date-range', dateRange);
  }, [dateRange]);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        // Create dates in Myanmar timezone (UTC+6:30)
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        
        // Set time to start and end of day
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);

        const stats = await dashboardService.getTicketStats({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });

        setStats(stats);
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError(t('dashboard.failedToLoadStats', 'Failed to load dashboard statistics'));
      } finally {
        setLoading(false);
      }
    };

    if (dateRange.startDate && dateRange.endDate) {
      fetchDashboardStats();
    }
  }, [dateRange, t]);

  const handleSearch = async (criteria: string, value: string) => {
    if (!value.trim()) {
      setSearchResults([]);
      setSelectedResultIndex(-1);
      return;
    }

    try {
      const results = await ticketService.searchTickets(criteria, value, 1, 10);
      setSearchResults(results.tickets);
      setSelectedResultIndex(-1);
    } catch (error) {
      console.error(t('dashboard.searchError', 'Search error'), error);
      setSearchResults([]);
    }
  };

  const handleSearchSelect = (ticketId: number) => {
    navigate(`/tickets/${ticketId}`);
    setSearchResults([]);
    setSelectedResultIndex(-1);
  };



  if (loading) {
    return (
      <DashboardLayout>
        <Container className="py-6">
          <div className="space-y-6">
            {/* Header Skeleton */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className={`h-8 w-64 rounded animate-pulse ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
              }`}></div>
              <div className={`h-10 w-80 rounded animate-pulse ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
              }`}></div>
            </div>

            {/* Stats Cards Skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className={`rounded-lg shadow p-6 animate-pulse ${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                }`}>
                  <div className={`h-4 w-24 rounded mb-2 ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  }`}></div>
                  <div className={`h-8 w-16 rounded ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  }`}></div>
                </div>
              ))}
            </div>

            {/* Charts Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className={`rounded-lg shadow p-6 animate-pulse ${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                }`}>
                  <div className={`h-6 w-32 rounded mb-4 ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  }`}></div>
                  <div className={`h-64 w-full rounded ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                  }`}></div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Container className="py-6">
          <div className={`rounded-lg shadow p-6 text-center ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="text-red-500 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className={`text-lg font-medium mb-2 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {t('dashboard.errorTitle', 'Error Loading Dashboard')}
            </h3>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              {error}
            </p>
          </div>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container className="py-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 sm:gap-6">
            <div className="flex-1 min-w-0">
              <h1 className={`text-xl sm:text-2xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {t('pageHeaders.dashboard.enhancedTitle', 'Enhanced Dashboard')}
              </h1>
              <p className={`mt-1 text-sm sm:text-base ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {t('pageHeaders.dashboard.enhancedSubtitle', 'Advanced analytics and insights for your tickets')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 lg:flex-shrink-0">
              <div className="order-2 sm:order-1">
                <DateRangePicker
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  onDateChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
                />
              </div>
              <div className="relative order-1 sm:order-2 w-full sm:w-80 lg:w-96">
                <SearchBar
                  onSearch={(criteria, value) => handleSearch(criteria, value)}
                  autoFocus={true}
                />
                {searchResults.length > 0 && (
                  <div className={`absolute top-full left-0 right-0 mt-1 rounded-md shadow-lg z-50 max-h-80 sm:max-h-96 overflow-y-auto ${
                    theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                  }`}>
                    {searchResults.map((ticket, index) => (
                      <div
                        key={ticket.id}
                        className={`px-3 sm:px-4 py-2 sm:py-3 cursor-pointer border-b last:border-b-0 ${
                          index === selectedResultIndex
                            ? theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                            : theme === 'dark' ? 'hover:bg-gray-700 border-gray-700' : 'hover:bg-gray-50 border-gray-200'
                        }`}
                        onClick={() => handleSearchSelect(ticket.id)}
                      >
                        <div className={`font-medium text-sm sm:text-base truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          #{ticket.id} - {ticket.title}
                        </div>
                        <div className={`text-xs sm:text-sm mt-1 ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          {ticket.status} {t('pageHeaders.dashboard.separator', '•')} {ticket.priority}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <TenantInfo />

          

          {/* Performance Metrics Cards */}
          {stats && <PerformanceMetrics stats={stats} />}

          {/* Main Charts Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 xl:items-stretch">
            {/* Ticket Trends - Takes 2 columns */}
            <div className="xl:col-span-2 flex">
              <EnhancedTicketTrends dateRange={dateRange} />
            </div>
            
            {/* Top Contributors */}
            <div className="xl:col-span-1 flex">
              <TopContributorsCard dateRange={dateRange} />
            </div>
          </div>

          {/* Secondary Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Breakdown */}
            <TicketStatusBreakdown stats={stats} />
            
            {/* Priority Distribution */}
            <PriorityDistribution stats={stats} />
          </div>

          {/* Omnichannel Overview - Only shows if there are active channels */}
          <OmnichannelOverview />

          {/* Agent Performance - Only shows if there are omnichannel agents */}
          {/* <AgentPerformance /> */}
        </div>
      </Container>
    </DashboardLayout>
  );
}
